import type { PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import {
  IcoChevronLeft,
  IcoChevronRight,
  IcoImage,
  IcoPlus,
} from "../../components/icons";
import { AppLayout } from "../../components/layout";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { MimicState } from "../../stores/mimic";
import type { OrganizationListItem, User } from "../../types";

type OrgsState = {
  currentUser: User;
  mimic: MimicState;
  organizations: OrganizationListItem[];
};

export async function load(): Promise<OrgsState> {
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    organizations: orgsResult.organizations,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Workspaces");
  const { currentUser, mimic, organizations } = state.value;

  return (
    <AppLayout user={currentUser} mimic={mimic}>
      <div class="flex justify-center">
        <div class="w-full max-w-2xl flex flex-col gap-6">
          <a
            href="/"
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
          >
            <IcoChevronLeft class="-ml-[5px] size-4" />
            Back
          </a>

          <div class="flex items-center justify-between">
            <h1 class="text-2xl font-bold text-text">Organizations</h1>
            <a
              href="/orgs/new"
              class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium bg-surface-hover hover:bg-surface-active text-text-secondary transition-colors"
            >
              <IcoPlus class="size-4" />
              New Organization
            </a>
          </div>

          {organizations.length === 0 ? (
            <div class="bg-surface border border-border rounded-lg p-8 text-center">
              <p class="text-text-muted mb-4">
                You're not a member of any organizations yet.
              </p>
              <a
                href="/orgs/new"
                class="inline-flex items-center gap-2 text-sm font-medium text-text hover:text-text-secondary"
              >
                <IcoPlus class="size-4" />
                Create your first organization
              </a>
            </div>
          ) : (
            <div class="flex flex-col gap-2">
              {organizations.map((org) => (
                <a
                  key={org.id}
                  href={`/orgs/${org.id}/members`}
                  class="group flex items-center gap-3 p-3 rounded-xl bg-surface-hover hover:bg-surface-active transition-colors"
                >
                  <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:ring-2 group-hover:ring-text-subtle transition-shadow">
                    {org.logoUrl ? (
                      <img
                        src={org.logoUrl}
                        alt={org.name}
                        class="size-full object-cover"
                      />
                    ) : (
                      <IcoImage class="size-5 text-text-muted" />
                    )}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-medium text-text truncate">{org.name}</p>
                    <p class="text-sm text-text-muted">
                      {org.memberCount}{" "}
                      {org.memberCount === 1 ? "member" : "members"}
                    </p>
                  </div>
                  <IcoChevronRight class="size-4 text-text-subtle" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
